import type { ComponentProps, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Callout, Checkbox, Icon, IconButton, RadioButton, RadioGroup, Tab, TabGroup, Table, Tag, TextField, Toast } from '@humanforce/mango-ui-react';
import type { TableColumn, ToastRef } from '@humanforce/mango-ui-react';
import { PageTitle } from '../components/layout/PageTitle';
import { Flyout } from '../components/ui';
import { SelectField } from '@pay-roadrunner/form-fields-ui';
import { buildGeneralParametersViewModel } from '../features/generalParameters/generalParametersModel';
import {
  createGeneralParameter,
  createParameterValue,
  fetchGeneralParameterList,
  fetchGeneralParameterMap,
  fetchGeneralParameterMirror,
  fetchGeneralParameterOverview,
  fetchRegisteredTargets,
  updateParameterMapEntry,
} from '../features/generalParameters/generalParametersHttpClient';
import { fetchRegions } from '../features/regions/regionsHttpClient';

import type { GeneralParametersOverviewResponse, ListDetailResponse, MapDetailResponse, MirrorDetailResponse, ParameterMapEntryDto, ParameterOverviewRow, ParameterValueDto, RegisteredTargetDto, RegionDto } from '../features/generalParameters/generalParametersApiTypes';
import type { GeneralParameterRowModel } from '../features/generalParameters/generalParametersModel';

const listValueColumns: TableColumn[] = [
  { field: 'code', header: 'Code', sortable: true },
  { field: 'displayLabel', header: 'Label', sortable: true },
  { field: 'effectiveStartDate', header: 'Effective', body: (row: ParameterValueDto) => row.effectiveStartDate.slice(0, 10) },
  { field: 'canEdit', header: 'Actions', body: (row: ParameterValueDto) => row.canEdit ? 'Editable' : 'Read-only' },
];

const mirrorValueColumns: TableColumn[] = [
  { field: 'code', header: 'Code' },
  { field: 'displayLabel', header: 'Label' },
  { field: 'effectiveStartDate', header: 'Effective', body: (row: ParameterValueDto) => row.effectiveStartDate.slice(0, 10) },
];

const parameterTableColumns: TableColumn[] = [
  {
    field: 'title',
    header: 'Parameter',
    sortable: true,
    body: (row: GeneralParameterRowModel) => (
      <>
        <span className="mgo-weight-strong mgo-color-neutral" style={{ display: 'block' }}>{row.title}</span>
        {row.subtitle && <span className="mgo-color-muted" style={{ display: 'block' }}>{row.subtitle}</span>}
      </>
    ),
  },
  { field: 'kindLabel', header: 'Kind', sortable: true },
  { field: 'category', header: 'Category', sortable: true },
  { field: 'entries', header: 'Entries', sortable: true },
  { field: 'regionLabel', header: 'Region' },
  { field: 'source', header: 'Source' },
  { field: 'lastModifiedBy', header: 'User' },
  { field: 'lastModified', header: 'Last modified', sortable: true },
];

const emptyOverview: GeneralParametersOverviewResponse = {
  rows: [],
  filteredCount: 0,
  effectiveAsAt: new Date().toISOString(),
  filterCounts: {
    kinds: [],
    categories: [],
    regions: [],
    sources: [],
  },
};

interface FlyoutPanelProps {
  action?: ReactNode;
  children: ReactNode;
  icon: ComponentProps<typeof Icon>['name'];
  subtitle?: string;
  title: string;
}

const FlyoutPanel = ({ action, children, icon, subtitle, title }: FlyoutPanelProps) => (
  <Card
    header={(
      <div className="mgo-split mgo-align-start mgo-gap-l">
        <div className="mgo-cluster mgo-gap-m" style={{ minWidth: 0 }}>
          <span className="mgo-background-brand mgo-radius-strong mgo-padding-s" style={{ display: 'inline-flex', color: 'var(--mgo-color-fg-brand)' }}>
            <Icon name={icon} />
          </span>
          <div className="mgo-stack mgo-gap-xs" style={{ minWidth: 0 }}>
            <h3 className="mgo-text-m mgo-weight-strong mgo-color-neutral">{title}</h3>
            {subtitle && <p className="mgo-text-s mgo-color-muted">{subtitle}</p>}
          </div>
        </div>
        {action && <div style={{ flexShrink: 0 }}>{action}</div>}
      </div>
    )}
  >
    {children}
  </Card>
);

interface CreateDialogCardProps {
  children: ReactNode;
  icon: ComponentProps<typeof Icon>['name'];
  title: string;
}

const CreateDialogCard = ({ children, icon, title }: CreateDialogCardProps) => (
  <Card
    header={(
      <div className="mgo-cluster mgo-gap-m">
        <span className="mgo-background-brand mgo-radius-strong mgo-padding-s" style={{ display: 'inline-flex', color: 'var(--mgo-color-fg-brand)' }}>
          <Icon name={icon} />
        </span>
        <h3 className="mgo-text-m mgo-weight-strong mgo-color-neutral">{title}</h3>
      </div>
    )}
  >
    <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-l">{children}</div>
  </Card>
);

interface ToastMessage {
  id: number;
  message: string;
  tone: 'positive' | 'critical' | 'caution' | 'neutral';
}

const useToast = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = useCallback((message: string, tone: ToastMessage['tone'] = 'positive') => {
    setToasts((current) => [...current, { id: Date.now(), message, tone }]);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  return useMemo(() => ({ addToast, removeToast, toasts }), [addToast, removeToast, toasts]);
};

interface GeneralParametersToastContainerProps {
  onClose: (id: number) => void;
  toasts: ToastMessage[];
}

const GeneralParametersToastContainer = ({ onClose, toasts }: GeneralParametersToastContainerProps) => {
  const toastRef = useRef<ToastRef>(null);
  const shownToastIds = useRef(new Set<number>());

  useEffect(() => {
    toasts.forEach((toast) => {
      if (shownToastIds.current.has(toast.id)) return;
      shownToastIds.current.add(toast.id);
      toastRef.current?.show({
        tone: toast.tone,
        detail: toast.message,
        life: 3000,
      });
      window.setTimeout(() => onClose(toast.id), 3000);
    });
  }, [onClose, toasts]);

  return (
    <>
      <Toast ref={toastRef} />
      <div aria-live="polite" className="sr-only">
        {toasts.map((toast) => <div key={toast.id} role="status">{toast.message}</div>)}
      </div>
    </>
  );
};

export const GeneralParametersPage = () => {
  const [overview, setOverview] = useState<GeneralParametersOverviewResponse>(emptyOverview);
  const [regions, setRegions] = useState<RegionDto[]>([]);
  const [targets, setTargets] = useState<RegisteredTargetDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [targetsLoaded, setTargetsLoaded] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedKind, setSelectedKind] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [createForm, setCreateForm] = useState({
    fieldLabel: '',
    fieldCode: '',
    targetTable: '',
    parameterName: '',
    description: '',
    category: 'Person',
    region: '',
    effectiveFrom: '2026-06-17',
    isRequired: true,
    listMode: 'create',
  });
  const [valueForm, setValueForm] = useState({
    code: '',
    displayLabel: '',
    effectiveStartDate: '2026-07-01',
  });
  const [mappingDrafts, setMappingDrafts] = useState<Record<string, string>>({});
  const [detail, setDetail] = useState<
    | { kind: 'List'; data: ListDetailResponse }
    | { kind: 'Map'; data: MapDetailResponse }
    | { kind: 'Mirror'; data: MirrorDetailResponse }
    | null
  >(null);
  const [showAddValue, setShowAddValue] = useState(false);
  const { addToast, removeToast, toasts } = useToast();

  const loadOverview = async () => {
    setLoading(true);
    const [overviewResponse, regionsResponse] = await Promise.all([
      fetchGeneralParameterOverview({
        kind: selectedKind || undefined,
        search: query || undefined,
        category: selectedCategory || undefined,
        regionCode: selectedRegion || undefined,
      }),
      fetchRegions(),
    ]);
    setOverview(overviewResponse.result ?? emptyOverview);
    setRegions(regionsResponse.result ?? []);
    setCreateForm((current) => ({ ...current, region: current.region || regionsResponse.result?.[0]?.code || '' }));
    setError('');
    setLoading(false);
  };

  const refreshOverview = () => {
    void loadOverview().catch(() => {
      setError('Unable to load general parameters.');
      setLoading(false);
    });
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      fetchGeneralParameterOverview({
        kind: selectedKind || undefined,
        search: query || undefined,
        category: selectedCategory || undefined,
        regionCode: selectedRegion || undefined,
      }),
      fetchRegions(),
    ])
      .then(([overviewResponse, regionsResponse]) => {
        if (!active) return;
        setOverview(overviewResponse.result ?? emptyOverview);
        setRegions(regionsResponse.result ?? []);
        setCreateForm((current) => ({ ...current, region: current.region || regionsResponse.result?.[0]?.code || '' }));
        setError('');
      })
      .catch(() => {
        if (active) setError('Unable to load general parameters.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [query, selectedCategory, selectedKind, selectedRegion]);

  const viewModel = useMemo(() => buildGeneralParametersViewModel(overview), [overview]);
  const totalLabel = `${viewModel.totalCount} ${viewModel.totalCount === 1 ? 'Parameter' : 'Parameters'}`;

  const openCreate = () => {
    setFormError('');
    setCreateOpen(true);
    if (!targetsLoaded) {
      void fetchRegisteredTargets().then((response) => {
        setTargets(response.result ?? []);
        setTargetsLoaded(true);
      });
    }
  };

  useEffect(() => {
    if (!createOpen) return undefined;
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setCreateOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [createOpen]);

  const openDetail = async (row: ParameterOverviewRow) => {
    setFormError('');
    setShowAddValue(false);
    setValueForm({ code: '', displayLabel: '', effectiveStartDate: '2026-07-01' });
    if (row.kind === 'List') {
      const response = await fetchGeneralParameterList(row.code);
      if (response?.result) setDetail({ kind: 'List', data: response.result });
    } else if (row.kind === 'Map') {
      const response = await fetchGeneralParameterMap(row.code);
      if (response?.result) {
        setMappingDrafts(Object.fromEntries(response.result.mappings.map((mapping) => [mapping.id, mapping.targetCode])));
        setDetail({ kind: 'Map', data: response.result });
      }
    } else if (row.kind === 'Mirror') {
      const response = await fetchGeneralParameterMirror(row.code);
      if (response?.result) setDetail({ kind: 'Mirror', data: response.result });
    }
  };

  const openGridRow = (rowCode: string) => {
    const sourceRow = overview.rows.find((item) => item.code === rowCode);
    if (sourceRow) void openDetail(sourceRow);
  };

  const selectedSummary = detail?.data.summary;
  const selectedTargetTable = createForm.targetTable || targets[0]?.tableName || '';
  const selectedCreateRegion = createForm.region || regions[0]?.code || '';
  const target = targets.find((item) => item.tableName === selectedTargetTable);
  const disableCreateParameter = saving || !targetsLoaded || !target || selectedCreateRegion.trim() === '';
  const canCreateValue = detail?.kind === 'List' && valueForm.code.trim() !== '' && valueForm.displayLabel.trim() !== '' && valueForm.effectiveStartDate !== '' && !saving;

  const handleCreateParameter = async () => {
    if (!target || selectedCreateRegion.trim() === '') {
      setFormError('Select a target table and region before creating the parameter.');
      return;
    }
    if (createForm.fieldLabel.trim() === '' || createForm.fieldCode.trim() === '' || createForm.parameterName.trim() === '') {
      setFormError('Complete the required fields before creating the parameter.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      await createGeneralParameter({
        code: createForm.fieldCode.trim().toUpperCase().replace(/\s+/g, '_'),
        name: createForm.parameterName.trim(),
        description: createForm.description.trim() || undefined,
        category: createForm.category,
        regions: [selectedCreateRegion],
        field: {
          fieldCode: createForm.fieldCode.trim(),
          label: createForm.fieldLabel.trim(),
          tableName: target.tableName,
          screenRef: target.screenRef,
          isRequired: createForm.isRequired,
        },
        initialValues: [],
      });
      setCreateOpen(false);
      addToast('General parameter created.');
      refreshOverview();
    } catch {
      setFormError('Unable to create the parameter. Check the values and try again.');
      addToast('Unable to create the parameter.', 'critical');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateValue = async () => {
    if (detail?.kind !== 'List') return;
    setSaving(true);
    setFormError('');
    try {
      await createParameterValue(detail.data.summary.code, {
        code: valueForm.code.trim().toUpperCase().replace(/\s+/g, '_'),
        displayLabel: valueForm.displayLabel.trim(),
        effectiveStartDate: valueForm.effectiveStartDate,
        sortOrder: detail.data.values.length + 1,
        isActive: true,
      });
      const response = await fetchGeneralParameterList(detail.data.summary.code);
      if (response.result) setDetail({ kind: 'List', data: response.result });
      setShowAddValue(false);
      setValueForm({ code: '', displayLabel: '', effectiveStartDate: '2026-07-01' });
      setDetail(null);
      addToast('Value added.');
      refreshOverview();
    } catch {
      setFormError('Unable to add the value. Check the values and try again.');
      addToast('Unable to add the value.', 'critical');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMapping = async () => {
    if (detail?.kind !== 'Map') return;
    setSaving(true);
    setFormError('');
    try {
      await Promise.all(detail.data.mappings.filter((mapping) => mapping.canEdit).map((mapping) => updateParameterMapEntry(detail.data.summary.code, mapping.id, {
        targetCode: mappingDrafts[mapping.id] ?? mapping.targetCode,
        effectiveStartDate: mapping.effectiveStartDate,
        effectiveEndDate: mapping.effectiveEndDate,
        isActive: mapping.isActive,
      })));
      const response = await fetchGeneralParameterMap(detail.data.summary.code);
      if (response.result) {
        setMappingDrafts(Object.fromEntries(response.result.mappings.map((mapping) => [mapping.id, mapping.targetCode])));
        setDetail({ kind: 'Map', data: response.result });
      }
      setDetail(null);
      addToast('Mapping saved.');
      refreshOverview();
    } catch {
      setFormError('Unable to save mapping changes. Check the values and try again.');
      addToast('Unable to save mapping changes.', 'critical');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageTitle
        title="General parameters"
        subtitle={totalLabel}
        actions={(
          <>
            <Button disabled variant="secondary">Import</Button>
            <Button onClick={openCreate}>New parameter</Button>
          </>
        )}
      />

      {loading && <p className="mgo-text-s mgo-color-muted">Loading general parameters...</p>}
      {error && <Callout tone="critical">{error}</Callout>}

      {!loading && !error && (
        <div className="mgo-stack mgo-gap-l">
          <div className="mgo-grid mgo-gap-m mgo-grid-cols-tablet-4">
            {viewModel.summaryCards.map((card) => {
              const isSelected = selectedKind === card.key || (card.key === 'all' && selectedKind === '');
              return (
                <button
                  aria-pressed={isSelected}
                  key={card.key}
                  type="button"
                  className={`mgo-interactive mgo-padding-l mgo-stack mgo-gap-s ${isSelected ? 'mgo-background-brand mgo-border-brand' : 'mgo-background-raised mgo-border-neutral'}`}
                  style={{ textAlign: 'left' }}
                  onClick={() => setSelectedKind(card.key === 'all' ? '' : card.key)}
                >
                  <span className="mgo-text-s mgo-weight-strong mgo-color-muted">{card.label}</span>
                  <p className="mgo-weight-strong mgo-color-neutral" style={{ margin: 0, fontSize: 'var(--mgo-size-60)' }}>{card.count}</p>
                </button>
              );
            })}
          </div>

          <div className="mgo-cluster mgo-gap-s">
            <TextField id="parameter-search" label="Find a parameter..." value={query} onChange={(event) => setQuery(event.target.value)} />
            {viewModel.categoryFilters.map((filter) => (
              <button
                className="mgo-text-s mgo-weight-strong mgo-color-neutral mgo-background-raised mgo-border-neutral"
                key={filter.code}
                style={{ padding: 'var(--mgo-space-20) var(--mgo-space-30)' }}
                type="button"
                onClick={() => setSelectedCategory((current) => filter.code === 'all' || current === filter.code ? '' : filter.code)}
              >
                {filter.label}
              </button>
            ))}
            {viewModel.regionFilters.map((filter) => (
              <button
                className="mgo-text-s mgo-weight-strong mgo-color-neutral mgo-background-raised mgo-border-neutral"
                key={filter.code}
                style={{ padding: 'var(--mgo-space-20) var(--mgo-space-30)' }}
                type="button"
                onClick={() => setSelectedRegion((current) => filter.code === 'all' || current === filter.code ? '' : filter.code)}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {viewModel.rows.length === 0 ? (
            <div className="mgo-stack mgo-align-start mgo-gap-m">
              <h2 className="mgo-text-l mgo-weight-strong mgo-color-neutral">No parameters match this view</h2>
              <p className="mgo-text-s mgo-color-muted">Create a general parameter to add the first row to the grid.</p>
              {!createOpen && <Button onClick={openCreate}>Create parameter</Button>}
            </div>
          ) : (
            <Table<GeneralParameterRowModel>
              value={viewModel.rows}
              columns={parameterTableColumns}
              onRowClick={(row) => openGridRow(row.code)}
            />
          )}
        </div>
      )}

      {createOpen && (
        <div
          style={{
            alignItems: 'center',
            background: 'var(--mgo-color-bg-blanket)',
            display: 'flex',
            inset: 0,
            justifyContent: 'center',
            padding: 'var(--mgo-space-60)',
            position: 'fixed',
            zIndex: 50,
          }}
        >
          <section
            aria-label="New parameter"
            aria-modal="true"
            className="mgo-background-raised"
            role="dialog"
            style={{
              borderRadius: 'var(--mgo-radius-strong)',
              boxShadow: 'var(--mgo-shadow-floating)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: 'calc(100vh - 6rem)',
              maxWidth: '84rem',
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <header className="mgo-split mgo-align-start mgo-gap-xl" style={{ padding: 'var(--mgo-space-70) var(--mgo-space-80) var(--mgo-space-40)' }}>
              <div className="mgo-stack mgo-gap-xs">
                <h2 className="mgo-weight-strong mgo-color-neutral" style={{ margin: 0, fontSize: 'var(--mgo-size-60)' }}>New parameter</h2>
                <p className="mgo-text-m mgo-color-muted">List parameter</p>
              </div>
              <IconButton icon="close" label="Close New parameter" variant="tertiary" onClick={() => setCreateOpen(false)} />
            </header>
            <div className="mgo-stack mgo-gap-xl" style={{ flex: 1, overflowY: 'auto', padding: '0 var(--mgo-space-80) var(--mgo-space-60)' }}>
              <Callout tone="info" title="Field code and binding are permanent">
                You can rename, re-scope and change options later — just not the code or target binding.
              </Callout>
              {targetsLoaded && targets.length === 0 && (
                <Callout tone="caution">No registered targets are available for new parameters yet.</Callout>
              )}
              {formError && <Callout tone="critical">{formError}</Callout>}

              <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-xl">
                <CreateDialogCard icon="lock" title="Field">
                  <div style={{ gridColumn: '1 / -1' }}>
                    <TextField id="field-label" label="Field label" placeholder="Preferred payroll location" required value={createForm.fieldLabel} onChange={(event) => setCreateForm((current) => ({ ...current, fieldLabel: event.target.value }))} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <TextField id="field-code" label="Field code" placeholder="preferredPayrollLocation" required value={createForm.fieldCode} onChange={(event) => setCreateForm((current) => ({ ...current, fieldCode: event.target.value }))} />
                  </div>
                  <SelectField
                    disabled={targets.length === 0}
                    id="target-table"
                    label="Target table"
                    options={targets.length > 0 ? targets.map((target) => ({ label: target.tableName, value: target.tableName })) : [{ label: 'No registered targets', value: '' }]}
                    value={selectedTargetTable}
                    onChange={(targetTable) => setCreateForm((current) => ({ ...current, targetTable }))}
                  />
                  <SelectField
                    disabled={targets.length === 0}
                    id="target-screen"
                    label="Target screen"
                    options={target ? [{ label: target.screenRef, value: target.screenRef }] : [{ label: 'Select a registered target', value: '' }]}
                    value={target?.screenRef ?? ''}
                  />
                </CreateDialogCard>

                <CreateDialogCard icon="navigation-menu" title="List">
                  <div style={{ gridColumn: '1 / -1' }}>
                    <RadioGroup
                      label="List mode"
                      name="new-parameter-list-mode"
                      direction="horizontal"
                      variant="button"
                      value={createForm.listMode}
                      onChange={(listMode) => setCreateForm((current) => ({ ...current, listMode }))}
                    >
                      <RadioButton value="create" label="Create new list" />
                      <RadioButton value="reuse" label="Reuse existing list" />
                    </RadioGroup>
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <TextField id="parameter-name" label="Parameter name" placeholder="Payroll locations" required value={createForm.parameterName} onChange={(event) => setCreateForm((current) => ({ ...current, parameterName: event.target.value }))} />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <SelectField
                      id="category"
                      label="Category"
                      options={[
                        { label: 'Person', value: 'Person' },
                        { label: 'Employment', value: 'Employment' },
                        { label: 'Organisation', value: 'Organisation' },
                        { label: 'Lifecycle', value: 'Lifecycle' },
                        { label: 'Contact', value: 'Contact' },
                      ]}
                      value={createForm.category}
                      onChange={(category) => setCreateForm((current) => ({ ...current, category }))}
                    />
                  </div>
                </CreateDialogCard>
              </div>

              <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-xl" style={{ alignItems: 'start' }}>
                <RadioGroup
                  label="Region scope"
                  name="new-parameter-region-scope"
                  direction="horizontal"
                  variant="button"
                  value={selectedCreateRegion}
                  onChange={(region) => setCreateForm((current) => ({ ...current, region }))}
                >
                  {regions.map((region) => <RadioButton key={region.code} value={region.code} label={region.code} />)}
                </RadioGroup>
                <div style={{ maxWidth: '15rem' }}>
                  <TextField id="effective-from" label="Effective from" type="date" required value={createForm.effectiveFrom} onChange={(event) => setCreateForm((current) => ({ ...current, effectiveFrom: event.target.value }))} />
                </div>
              </div>

              <Checkbox
                id="new-parameter-required"
                checked={createForm.isRequired}
                label="This field is required when creating or updating worker records"
                onChange={(isRequired) => setCreateForm((current) => ({ ...current, isRequired }))}
              />
            </div>
            <footer className="mgo-cluster mgo-justify-end mgo-gap-m" style={{ borderTop: 'var(--mgo-border-neutral)', padding: 'var(--mgo-space-50) var(--mgo-space-80)' }}>
              <Button variant="secondary" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button disabled={disableCreateParameter} onClick={() => void handleCreateParameter()}>{saving ? 'Creating...' : 'Create parameter'}</Button>
            </footer>
          </section>
        </div>
      )}

      <Flyout
        heading={selectedSummary?.name ?? 'Parameter'}
        open={detail !== null}
        onClose={() => setDetail(null)}
        footer={detail?.kind === 'Map'
          ? (
            <>
              <Button variant="secondary" onClick={() => setDetail(null)}>Cancel</Button>
              <Button disabled={saving} onClick={() => void handleSaveMapping()}>{saving ? 'Saving...' : 'Save mapping'}</Button>
            </>
          )
          : <Button variant="secondary" onClick={() => setDetail(null)}>Close</Button>}
      >
        {detail?.kind === 'List' && (
          <div className="mgo-stack mgo-gap-l">
            <div className="mgo-stack mgo-gap-s">
              <p className="mgo-text-s mgo-color-muted">{detail.data.summary.description}</p>
              <div className="mgo-cluster mgo-gap-s">
                <Tag label={detail.data.summary.regions.map((region) => region.code).join(', ')} tone="grey" />
                <Tag label="List" tone="grey" />
              </div>
            </div>
            <FlyoutPanel
              action={detail.data.editability.canAddValues ? <Button onClick={() => setShowAddValue(true)}>Add value</Button> : undefined}
              icon="settings"
              subtitle={`${detail.data.values.length} values`}
              title="Values"
            >
              <div className="mgo-stack mgo-gap-l">
                <TextField id="list-search" label="Find a value..." value="" onChange={() => { }} />
                {showAddValue && (
                  <section className="mgo-background-brand mgo-padding-l mgo-radius-strong" style={{ border: 'var(--mgo-border-brand-soft)' }}>
                    <div className="mgo-stack mgo-gap-m">
                      <h3 className="mgo-text-m mgo-weight-strong mgo-color-neutral">Add a value to {detail.data.summary.name}</h3>
                      {formError && <Callout tone="critical">{formError}</Callout>}
                      <div className="mgo-grid mgo-gap-m mgo-grid-cols-tablet-2">
                        <TextField id="value-code" label="Code" value={valueForm.code} onChange={(event) => setValueForm((current) => ({ ...current, code: event.target.value }))} />
                        <TextField id="value-effective-from" label="Effective from" type="date" value={valueForm.effectiveStartDate} onChange={(event) => setValueForm((current) => ({ ...current, effectiveStartDate: event.target.value }))} />
                      </div>
                      <TextField id="value-label" label="Value" value={valueForm.displayLabel} onChange={(event) => setValueForm((current) => ({ ...current, displayLabel: event.target.value }))} />
                      <div className="mgo-cluster mgo-justify-end mgo-gap-s">
                        <Button variant="secondary" onClick={() => setShowAddValue(false)}>Cancel</Button>
                        <Button disabled={!canCreateValue} onClick={() => void handleCreateValue()}>{saving ? 'Adding...' : 'Add value'}</Button>
                      </div>
                    </div>
                  </section>
                )}
                <Table<ParameterValueDto>
                  value={detail.data.values}
                  columns={listValueColumns}
                  emptyMessage="No values yet."
                />
              </div>
            </FlyoutPanel>
          </div>
        )}
        {detail?.kind === 'Map' && (
          <div className="mgo-stack mgo-gap-l">
            <p className="mgo-text-s mgo-color-muted">{detail.data.summary.description}</p>
            <TabGroup>
              <Tab value="mapping" label="Mapping">
                <FlyoutPanel action={<Button disabled>Add mapping</Button>} icon="analytics-dashboard" subtitle={`${detail.data.mappings.length} mappings`} title="Mapping">
                  <div className="mgo-stack mgo-gap-l">
                    <TextField id="map-search" label="Find a value..." value="" onChange={() => { }} />
                    {formError && <Callout tone="critical">{formError}</Callout>}
                    <Table<ParameterMapEntryDto>
                      editMode="cell"
                      value={detail.data.mappings.map((m) => ({ ...m, targetCode: mappingDrafts[m.id] ?? m.targetCode }))}
                      columns={[
                        {
                          field: 'sourceValue',
                          header: 'Source',
                          body: (row: ParameterMapEntryDto) => row.sourceValue.displayLabel,
                        },
                        {
                          field: 'targetCode',
                          header: detail.data.summary.target ?? 'Target mapping',
                          body: (row: ParameterMapEntryDto) => detail.data.targetVocabulary.find((t) => t.code === row.targetCode)?.label ?? row.targetCode,
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          editor: (options: any) => (
                            <select
                              autoFocus
                              aria-label={detail.data.summary.target ?? 'Target mapping'}
                              className="select-control"
                              disabled={!(options.rowData as ParameterMapEntryDto).canEdit}
                              style={{ width: '100%' }}
                              value={options.value as string}
                              onChange={(e) => options.editorCallback(e.target.value)}
                            >
                              {detail.data.targetVocabulary.map((t) => (
                                <option key={t.code} value={t.code}>{t.label}</option>
                              ))}
                            </select>
                          ),
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          onCellEditComplete: (e: any) => setMappingDrafts((cur) => ({ ...cur, [(e.rowData as ParameterMapEntryDto).id]: e.newValue as string })),
                        },
                        {
                          field: 'effectiveStartDate',
                          header: 'Effective',
                          body: (row: ParameterMapEntryDto) => row.effectiveStartDate.slice(0, 10),
                        },
                      ]}
                    />
                  </div>
                </FlyoutPanel>
              </Tab>
              <Tab value="connections" label="Connections">
                <FlyoutPanel icon="share" title="Connections">
                  <Callout tone="neutral">Connection details are not yet available.</Callout>
                </FlyoutPanel>
              </Tab>
              <Tab value="activity" label="Activity">
                <FlyoutPanel icon="ledger" title="Activity">
                  <Callout tone="neutral">Activity history is not yet available.</Callout>
                </FlyoutPanel>
              </Tab>
            </TabGroup>
          </div>
        )}
        {detail?.kind === 'Mirror' && (
          <div className="mgo-stack mgo-gap-l">
            <p className="mgo-text-s mgo-weight-strong mgo-color-neutral">Read-only mirror</p>
            <p className="mgo-text-s mgo-color-muted">{detail.data.sourceMaster}</p>
            <FlyoutPanel icon="settings" subtitle={`${detail.data.values.length} synced values`} title="Mirror values">
              <Table<ParameterValueDto>
                value={detail.data.values}
                columns={mirrorValueColumns}
              />
            </FlyoutPanel>
          </div>
        )}
      </Flyout>
      <GeneralParametersToastContainer toasts={toasts} onClose={removeToast} />
    </>
  );
};
